import { ANALYSIS_THRESHOLDS } from './thresholds.js';
import type {
  AsyncPixelAnalysisOptions,
  BoundaryDominantColor,
  BoundingBox,
  CheckerboardDetection,
  CornerPixels,
  DetectionLabel,
  PixelAnalysisOptions,
  PixelColor,
  PixelStatistics,
  PngStructure,
  RgbaImageData,
  SolidBackgroundDetection,
  TransparentMargins,
} from './types.js';

interface ColorBucket {
  count: number;
  red: number;
  green: number;
  blue: number;
  alpha: number;
  quantized: PixelColor;
}

interface AnalysisAccumulator {
  transparent: number;
  semiTransparent: number;
  opaque: number;
  alphaSum: number;
  minAlpha: number;
  maxAlpha: number;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  edgePixels: number;
  edgeNonTransparent: number;
  boundaryColors: Map<string, ColorBucket>;
}

export interface ClassificationDetections {
  readonly solidBackground?: Pick<SolidBackgroundDetection, 'suspected'>;
  readonly checkerboard?: Pick<CheckerboardDetection, 'suspected'>;
}

/** Accepts a full parser result while documenting the three fields classification needs. */
export type PngClassificationInput = Pick<
  PngStructure,
  'hasAlphaChannel' | 'hasTrns' | 'supportsTransparency'
> &
  Partial<PngStructure>;

export function assertValidImageData(image: RgbaImageData): void {
  if (!Number.isSafeInteger(image.width) || image.width <= 0) {
    throw new RangeError('Image width must be a positive safe integer.');
  }
  if (!Number.isSafeInteger(image.height) || image.height <= 0) {
    throw new RangeError('Image height must be a positive safe integer.');
  }

  const totalPixels = image.width * image.height;
  const expectedLength = totalPixels * 4;
  if (!Number.isSafeInteger(totalPixels) || !Number.isSafeInteger(expectedLength)) {
    throw new RangeError('Image dimensions exceed the supported pixel range.');
  }
  if (!(image.data instanceof Uint8ClampedArray)) {
    throw new TypeError('Image data must be a Uint8ClampedArray.');
  }
  if (image.data.length !== expectedLength) {
    throw new RangeError(
      `RGBA data length mismatch: expected ${expectedLength}, received ${image.data.length}.`,
    );
  }
}

export function colorToHex(color: Pick<PixelColor, 'r' | 'g' | 'b'>): string {
  return `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`;
}

export function analyzePixels(
  image: RgbaImageData,
  options: PixelAnalysisOptions = {},
): PixelStatistics {
  assertValidImageData(image);
  const quantizationStep = normalizeQuantizationStep(options.boundaryQuantizationStep);
  const accumulator = createAccumulator();
  scanRows(image, 0, image.height, quantizationStep, accumulator);
  return finalizeStatistics(image, accumulator);
}

/**
 * Main-thread friendly counterpart to analyzePixels. It yields after each row
 * chunk; callers doing analysis in a Worker can use the synchronous function.
 */
export async function analyzePixelsAsync(
  image: RgbaImageData,
  options: AsyncPixelAnalysisOptions = {},
): Promise<PixelStatistics> {
  assertValidImageData(image);
  const quantizationStep = normalizeQuantizationStep(options.boundaryQuantizationStep);
  const requestedRows = options.rowsPerChunk ?? ANALYSIS_THRESHOLDS.asyncAnalysis.rowsPerChunk;
  if (!Number.isSafeInteger(requestedRows) || requestedRows <= 0) {
    throw new RangeError('rowsPerChunk must be a positive integer.');
  }

  const rowsPerChunk = Math.min(requestedRows, image.height);
  const accumulator = createAccumulator();
  options.onProgress?.(0);

  for (let startY = 0; startY < image.height; startY += rowsPerChunk) {
    throwIfAborted(options.signal);
    const endY = Math.min(image.height, startY + rowsPerChunk);
    scanRows(image, startY, endY, quantizationStep, accumulator);
    options.onProgress?.(endY / image.height);

    if (endY < image.height) {
      if (options.yieldToMainThread) {
        await options.yieldToMainThread();
      } else {
        await yieldTask();
      }
    }
  }

  throwIfAborted(options.signal);
  return finalizeStatistics(image, accumulator);
}

export function classifyAnalysis(
  png: PngClassificationInput,
  pixels: PixelStatistics,
  detections: ClassificationDetections = {},
): DetectionLabel[] {
  const labels: DetectionLabel[] = [];
  const hasRealTransparency = pixels.nonOpaquePixels > 0;

  if (hasRealTransparency) labels.push('REAL_TRANSPARENCY');
  if (pixels.fullyTransparentPixels > 0) labels.push('FULL_TRANSPARENCY_PRESENT');
  if (pixels.semiTransparentPixels > 0) labels.push('SEMI_TRANSPARENT_PIXELS');

  if (!hasRealTransparency) {
    if (png.supportsTransparency || png.hasAlphaChannel || png.hasTrns) {
      labels.push('ALPHA_CHANNEL_UNUSED');
    }
    labels.push('FULLY_OPAQUE');
  }

  if (pixels.edgeNonTransparentRatio >= ANALYSIS_THRESHOLDS.classification.edgeContentRatio) {
    labels.push('EDGE_CONTENT');
  }

  if (hasSignificantTransparentPadding(pixels)) {
    labels.push('TRANSPARENT_PADDING');
  }

  if (detections.solidBackground?.suspected) {
    labels.push('SUSPECTED_SOLID_BACKGROUND');
  }
  if (detections.checkerboard?.suspected) {
    labels.push('SUSPECTED_BAKED_CHECKERBOARD');
  }
  if (
    hasRealTransparency &&
    !detections.solidBackground?.suspected &&
    !detections.checkerboard?.suspected
  ) {
    labels.push('CLEAN_ASSET');
  }

  return labels;
}

/** Compatibility alias for consumers that use a more explicit name. */
export const analyzePixelData = analyzePixels;

function createAccumulator(): AnalysisAccumulator {
  return {
    transparent: 0,
    semiTransparent: 0,
    opaque: 0,
    alphaSum: 0,
    minAlpha: 255,
    maxAlpha: 0,
    minX: Number.POSITIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxX: -1,
    maxY: -1,
    edgePixels: 0,
    edgeNonTransparent: 0,
    boundaryColors: new Map(),
  };
}

function scanRows(
  image: RgbaImageData,
  startY: number,
  endY: number,
  quantizationStep: number,
  accumulator: AnalysisAccumulator,
): void {
  const { data, width, height } = image;
  for (let y = startY; y < endY; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      const alpha = data[offset + 3] ?? 0;

      accumulator.alphaSum += alpha;
      accumulator.minAlpha = Math.min(accumulator.minAlpha, alpha);
      accumulator.maxAlpha = Math.max(accumulator.maxAlpha, alpha);

      if (alpha === ANALYSIS_THRESHOLDS.alpha.transparent) {
        accumulator.transparent += 1;
      } else {
        if (alpha === ANALYSIS_THRESHOLDS.alpha.opaque) {
          accumulator.opaque += 1;
        } else {
          accumulator.semiTransparent += 1;
        }

        accumulator.minX = Math.min(accumulator.minX, x);
        accumulator.minY = Math.min(accumulator.minY, y);
        accumulator.maxX = Math.max(accumulator.maxX, x);
        accumulator.maxY = Math.max(accumulator.maxY, y);
      }

      if (isBoundaryPixel(x, y, width, height)) {
        accumulator.edgePixels += 1;
        if (alpha > ANALYSIS_THRESHOLDS.alpha.transparent) {
          accumulator.edgeNonTransparent += 1;
        }
        addBoundaryColor(data, offset, quantizationStep, accumulator.boundaryColors);
      }
    }
  }
}

function finalizeStatistics(
  image: RgbaImageData,
  accumulator: AnalysisAccumulator,
): PixelStatistics {
  const totalPixels = image.width * image.height;
  const contentBoundingBox = makeBoundingBox(accumulator);
  const transparentMargins = makeMargins(image, contentBoundingBox);
  const corners = readCorners(image);
  const boundaryDominantColor = dominantBoundaryColor(accumulator.boundaryColors);
  const edgeRatio = accumulator.edgePixels
    ? accumulator.edgeNonTransparent / accumulator.edgePixels
    : 0;

  return {
    totalPixels,
    fullyTransparentPixels: accumulator.transparent,
    semiTransparentPixels: accumulator.semiTransparent,
    fullyOpaquePixels: accumulator.opaque,
    nonOpaquePixels: accumulator.transparent + accumulator.semiTransparent,
    transparentPercentage: percentage(accumulator.transparent, totalPixels),
    fullyTransparentPercentage: percentage(accumulator.transparent, totalPixels),
    semiTransparentPercentage: percentage(accumulator.semiTransparent, totalPixels),
    nonOpaquePercentage: percentage(
      accumulator.transparent + accumulator.semiTransparent,
      totalPixels,
    ),
    minAlpha: accumulator.minAlpha,
    maxAlpha: accumulator.maxAlpha,
    averageAlpha: accumulator.alphaSum / totalPixels,
    contentBoundingBox,
    boundingBox: contentBoundingBox,
    transparentMargins,
    margins: transparentMargins,
    edgePixelCount: accumulator.edgePixels,
    edgeNonTransparentPixels: accumulator.edgeNonTransparent,
    edgeNonTransparentRatio: edgeRatio,
    edgeOpaqueRatio: edgeRatio,
    corners,
    boundaryDominantColor,
  };
}

function makeBoundingBox(accumulator: AnalysisAccumulator): BoundingBox | null {
  if (accumulator.maxX < 0 || accumulator.maxY < 0) return null;

  return {
    x: accumulator.minX,
    y: accumulator.minY,
    width: accumulator.maxX - accumulator.minX + 1,
    height: accumulator.maxY - accumulator.minY + 1,
    left: accumulator.minX,
    top: accumulator.minY,
    right: accumulator.maxX,
    bottom: accumulator.maxY,
  };
}

function makeMargins(image: RgbaImageData, box: BoundingBox | null): TransparentMargins {
  if (!box) {
    return { top: image.height, right: image.width, bottom: image.height, left: image.width };
  }

  return {
    top: box.top,
    right: image.width - box.right - 1,
    bottom: image.height - box.bottom - 1,
    left: box.left,
  };
}

function readCorners(image: RgbaImageData): CornerPixels {
  return {
    topLeft: readPixel(image, 0, 0),
    topRight: readPixel(image, image.width - 1, 0),
    bottomRight: readPixel(image, image.width - 1, image.height - 1),
    bottomLeft: readPixel(image, 0, image.height - 1),
  };
}

function readPixel(image: RgbaImageData, x: number, y: number): PixelColor {
  const offset = (y * image.width + x) * 4;
  return {
    r: image.data[offset] ?? 0,
    g: image.data[offset + 1] ?? 0,
    b: image.data[offset + 2] ?? 0,
    a: image.data[offset + 3] ?? 0,
  };
}

function addBoundaryColor(
  data: Uint8ClampedArray,
  offset: number,
  step: number,
  buckets: Map<string, ColorBucket>,
): void {
  const r = data[offset] ?? 0;
  const g = data[offset + 1] ?? 0;
  const b = data[offset + 2] ?? 0;
  const a = data[offset + 3] ?? 0;
  const quantized: PixelColor = {
    r: quantize(r, step),
    g: quantize(g, step),
    b: quantize(b, step),
    a: quantize(a, step),
  };
  const key = `${quantized.r},${quantized.g},${quantized.b},${quantized.a}`;
  const bucket = buckets.get(key);

  if (bucket) {
    bucket.count += 1;
    bucket.red += r;
    bucket.green += g;
    bucket.blue += b;
    bucket.alpha += a;
  } else {
    buckets.set(key, {
      count: 1,
      red: r,
      green: g,
      blue: b,
      alpha: a,
      quantized,
    });
  }
}

function dominantBoundaryColor(
  buckets: ReadonlyMap<string, ColorBucket>,
): BoundaryDominantColor | null {
  let dominant: ColorBucket | null = null;
  let total = 0;

  for (const bucket of buckets.values()) {
    total += bucket.count;
    if (!dominant || bucket.count > dominant.count) dominant = bucket;
  }
  if (!dominant || total === 0) return null;

  const color: PixelColor = {
    r: Math.round(dominant.red / dominant.count),
    g: Math.round(dominant.green / dominant.count),
    b: Math.round(dominant.blue / dominant.count),
    a: Math.round(dominant.alpha / dominant.count),
  };
  const ratio = dominant.count / total;
  return {
    color,
    quantizedColor: dominant.quantized,
    hex: colorToHex(color),
    count: dominant.count,
    total,
    ratio,
    percentage: ratio * 100,
  };
}

function hasSignificantTransparentPadding(pixels: PixelStatistics): boolean {
  const box = pixels.contentBoundingBox;
  if (!box) return false;

  const margins = pixels.transparentMargins;
  const largestMargin = Math.max(margins.top, margins.right, margins.bottom, margins.left);
  const relevantDimension = Math.max(
    box.width + margins.left + margins.right,
    box.height + margins.top + margins.bottom,
  );
  return (
    largestMargin >= ANALYSIS_THRESHOLDS.classification.transparentPaddingMinPixels &&
    largestMargin / relevantDimension >=
      ANALYSIS_THRESHOLDS.classification.transparentPaddingMinFraction
  );
}

function isBoundaryPixel(x: number, y: number, width: number, height: number): boolean {
  return x === 0 || y === 0 || x === width - 1 || y === height - 1;
}

function normalizeQuantizationStep(step?: number): number {
  const value = step ?? ANALYSIS_THRESHOLDS.boundary.quantizationStep;
  if (!Number.isSafeInteger(value) || value < 1 || value > 255) {
    throw new RangeError('boundaryQuantizationStep must be an integer from 1 to 255.');
  }
  return value;
}

function quantize(channel: number, step: number): number {
  return Math.min(255, Math.round(channel / step) * step);
}

function percentage(count: number, total: number): number {
  return total === 0 ? 0 : (count / total) * 100;
}

function toHex(channel: number): string {
  return Math.round(channel).toString(16).padStart(2, '0');
}

function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  if (typeof DOMException !== 'undefined') {
    throw new DOMException('Pixel analysis was aborted.', 'AbortError');
  }
  const error = new Error('Pixel analysis was aborted.');
  error.name = 'AbortError';
  throw error;
}

function yieldTask(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}
